import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { supabase } from '@/lib/supabase';

// Module-level constants - never recreated
const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: 5 }, (_, i) => {
  const year = CURRENT_YEAR - 2 + i;
  return { value: year, label: year.toString() };
});

// Default color for unknown leave types
const DEFAULT_LEAVE_COLOR = 'bg-gray-100 text-gray-800 border-gray-200';

// Convert hex color to Tailwind-like classes
function hexToTailwindClasses(hexColor) {
  if (!hexColor) return DEFAULT_LEAVE_COLOR;

  const colorMap = {
    // Standard system colors
    '#8B5CF6': 'bg-violet-100 text-violet-800 border-violet-200',
    '#EF4444': 'bg-red-100 text-red-800 border-red-200',
    '#3B82F6': 'bg-blue-100 text-blue-800 border-blue-200',
    '#10B981': 'bg-emerald-100 text-emerald-800 border-emerald-200',
    '#6B7280': 'bg-gray-100 text-gray-800 border-gray-200',
    '#F59E0B': 'bg-amber-100 text-amber-800 border-amber-200',
    '#9CA3AF': 'bg-gray-100 text-gray-600 border-gray-200',
    // Custom colors from database
    '#5FF7DD': 'bg-cyan-100 text-cyan-800 border-cyan-200',
    '#037720': 'bg-green-100 text-green-800 border-green-200',
    '#00FF33': 'bg-green-100 text-green-800 border-green-200',
    // Additional common colors
    '#EC4899': 'bg-pink-100 text-pink-800 border-pink-200',
    '#F97316': 'bg-orange-100 text-orange-800 border-orange-200',
    '#84CC16': 'bg-lime-100 text-lime-800 border-lime-200',
    '#06B6D4': 'bg-cyan-100 text-cyan-800 border-cyan-200',
    '#8B5CF6': 'bg-violet-100 text-violet-800 border-violet-200',
    '#A855F7': 'bg-purple-100 text-purple-800 border-purple-200',
    '#14B8A6': 'bg-teal-100 text-teal-800 border-teal-200',
    '#6366F1': 'bg-indigo-100 text-indigo-800 border-indigo-200',
  };

  const upperHex = hexColor.toUpperCase();
  if (colorMap[upperHex]) {
    return colorMap[upperHex];
  }

  // For unknown hex colors, generate based on brightness
  // Parse hex to RGB
  const hex = upperHex.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  // Calculate luminance to determine if color is light or dark
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  // Return a generic class that works with the hex color
  // The component will need to apply the actual color via style
  return luminance > 0.5
    ? 'bg-white border-2 text-gray-900'
    : 'bg-gray-100 border-2 text-gray-900';
}

// Get raw hex color for inline styling
function getHexColor(hexColor) {
  return hexColor || '#9CA3AF';
}

// Generate abbreviation from leave type name
function generateAbbreviation(name) {
  if (!name) return 'L';

  const knownAbbr = {
    'Annual Leave': 'AL',
    'Sick Leave': 'SL',
    'Unpaid Leave': 'UL',
    'Work From Home': 'WFH',
    'Parental Leave': 'PL',
    'Personal Leave': 'PER',
    'Bereavement Leave': 'BL',
    'Study Leave': 'STU',
    'Birthday Leave': 'BD',
    'Christmas Shutdown': 'XMS',
    'Christmas Shut Down': 'XMS',
  };

  if (knownAbbr[name]) return knownAbbr[name];

  const words = name.split(' ').filter(w => w.length > 0);
  if (words.length === 1) {
    return words[0].substring(0, 2).toUpperCase();
  }
  return words.map(w => w[0]).join('').toUpperCase().substring(0, 3);
}

/**
 * Hook to load team overview page data in a single optimized RPC call
 * Replaces 6 sequential queries with 1 call
 *
 * @param {string} companyId - Company ID
 * @param {number} year - Year to fetch data for
 * @param {Object} options - Configuration options
 * @param {string} options.userId - Current user's profile ID (for leave type visibility)
 * @param {boolean} options.canManageAttendance - Whether user has attendance.manage permission
 * @returns {Object} - { data, loading, error, refetch, teamMembers, stats, ... }
 */
export function useTeamOverview(companyId, year, options = {}) {
  const { userId = null, canManageAttendance = false } = options;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const loadingRef = useRef(false);
  const abortControllerRef = useRef(null);

  /**
   * Load team overview data using the optimized RPC
   */
  const loadData = useCallback(async () => {
    if (!companyId || !year) {
      console.log('[useTeamOverview] Missing companyId or year');
      setLoading(false);
      return null;
    }

    // Prevent concurrent requests
    if (loadingRef.current) {
      console.log('[useTeamOverview] Already loading, skipping...');
      return null;
    }

    // Cancel any pending request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    loadingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      console.log('[useTeamOverview] Loading data with single RPC call...');
      console.time('[useTeamOverview] RPC call duration');

      // Single RPC call replaces 6 sequential queries!
      const { data: rpcData, error: rpcError } = await supabase.rpc(
        'get_team_overview_page',
        {
          p_company_id: companyId,
          p_year: year,
          p_is_admin: canManageAttendance
        }
      );

      console.timeEnd('[useTeamOverview] RPC call duration');

      if (rpcError) {
        console.error('[useTeamOverview] RPC error:', rpcError);
        setError(rpcError.message);
        setData(null);
        return null;
      }

      console.log('[useTeamOverview] RPC response:', {
        teamMembersCount: rpcData?.team_members?.length || 0,
        leaveTypesCount: rpcData?.leave_types?.length || 0,
        leaveRequestsCount: rpcData?.leave_requests?.length || 0,
        stats: rpcData?.stats
      });

      setData(rpcData);
      return rpcData;
    } catch (err) {
      // Ignore abort errors
      if (err.name === 'AbortError') {
        return null;
      }
      console.error('[useTeamOverview] Error:', err);
      setError(err.message);
      setData(null);
      return null;
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, [companyId, year, canManageAttendance]);

  // Auto-load when dependencies change
  useEffect(() => {
    loadData();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [loadData]);

  /**
   * Find leave type config by name or ID
   */
  const findLeaveTypeConfig = useCallback((leaveType) => {
    const leaveTypes = data?.leave_types || [];
    return leaveTypes.find(lt =>
      lt.name === leaveType ||
      lt.id === leaveType ||
      lt.name.toLowerCase().replace(/\s+/g, '_') === leaveType
    );
  }, [data?.leave_types]);

  /**
   * Check if a leave type should be hidden from the current user
   */
  const shouldHideLeaveType = useCallback((leaveType, requestOwnerId) => {
    // If user is viewing their own leave, always show the full type
    if (requestOwnerId === userId) {
      return false;
    }

    // If user is admin/manager, always show the full type
    if (canManageAttendance) {
      return false;
    }

    // Find the leave type configuration
    const leaveTypeConfig = findLeaveTypeConfig(leaveType);

    // If leave type is admin-only, hide it from non-admins
    if (leaveTypeConfig?.required_permission === 'attendance.manage') {
      return true;
    }

    return false;
  }, [findLeaveTypeConfig, userId, canManageAttendance]);

  /**
   * Get leave type color - uses dynamic colors from database
   */
  const getLeaveTypeColor = useCallback((type, requestOwnerId) => {
    if (shouldHideLeaveType(type, requestOwnerId)) {
      return DEFAULT_LEAVE_COLOR;
    }

    // Find the leave type config to get its color
    const leaveTypeConfig = findLeaveTypeConfig(type);

    if (leaveTypeConfig?.color) {
      return hexToTailwindClasses(leaveTypeConfig.color);
    }

    return DEFAULT_LEAVE_COLOR;
  }, [shouldHideLeaveType, findLeaveTypeConfig]);

  /**
   * Get leave type abbreviation - generates from name
   */
  const getLeaveTypeAbbr = useCallback((type, requestOwnerId) => {
    if (shouldHideLeaveType(type, requestOwnerId)) {
      return 'L';
    }

    // The type IS the name in our system
    if (typeof type === 'string') {
      return generateAbbreviation(type);
    }

    return 'L';
  }, [shouldHideLeaveType]);

  /**
   * Get raw hex color for inline styling
   */
  const getLeaveTypeHexColor = useCallback((type, requestOwnerId) => {
    if (shouldHideLeaveType(type, requestOwnerId)) {
      return '#9CA3AF'; // Gray for hidden types
    }

    const leaveTypeConfig = findLeaveTypeConfig(type);
    return leaveTypeConfig?.color || '#9CA3AF';
  }, [shouldHideLeaveType, findLeaveTypeConfig]);

  /**
   * Get usage percentage for a team member
   */
  const getUsagePercentage = useCallback((member) => {
    if (!member?.entitled || member.entitled === 0) return 0;
    return Math.round((member.taken / member.entitled) * 100);
  }, []);

  /**
   * Get usage color based on percentage
   */
  const getUsageColor = useCallback((percentage) => {
    if (percentage >= 80) return 'text-red-600';
    if (percentage >= 60) return 'text-orange-600';
    return 'text-green-600';
  }, []);

  // Memoized: Off this week data
  const offThisWeek = useMemo(() => {
    if (!data?.leave_requests || !data?.date_context) return { users: [], count: 0 };

    const startOfWeek = new Date(data.date_context.start_of_week);
    const endOfWeek = new Date(data.date_context.end_of_week);

    const thisWeekRequests = data.leave_requests.filter(req => {
      const start = new Date(req.start_date);
      const end = new Date(req.end_date);
      return start <= endOfWeek && end >= startOfWeek;
    });

    // Group by user
    const byUser = {};
    thisWeekRequests.forEach(req => {
      if (!byUser[req.user_id]) {
        byUser[req.user_id] = {
          user: {
            id: req.user_id,
            first_name: req.first_name,
            last_name: req.last_name
          },
          requests: []
        };
      }
      byUser[req.user_id].requests.push(req);
    });

    return {
      users: Object.values(byUser),
      count: Object.keys(byUser).length
    };
  }, [data?.leave_requests, data?.date_context]);

  // Memoized: Upcoming leave (next 2 weeks)
  const upcomingLeave = useMemo(() => {
    if (!data?.leave_requests || !data?.date_context) return [];

    const today = new Date(data.date_context.today);
    const twoWeeksFromNow = new Date(data.date_context.two_weeks_from_now);

    return data.leave_requests
      .filter(req => {
        const start = new Date(req.start_date);
        return start > today && start <= twoWeeksFromNow;
      })
      .sort((a, b) => new Date(a.start_date) - new Date(b.start_date))
      .slice(0, 5)
      .map(req => ({
        ...req,
        daysUntil: Math.ceil((new Date(req.start_date) - today) / (1000 * 60 * 60 * 24))
      }));
  }, [data?.leave_requests, data?.date_context]);

  // Memoized: This month's leave
  const thisMonthLeave = useMemo(() => {
    if (!data?.leave_requests || !data?.date_context) return { requests: [], uniquePeople: 0, totalDays: 0 };

    const today = new Date(data.date_context.today);
    const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);

    const requests = data.leave_requests
      .filter(req => {
        const start = new Date(req.start_date);
        const end = new Date(req.end_date);
        return start <= endOfMonth && end >= startOfMonth;
      })
      .sort((a, b) => new Date(a.start_date) - new Date(b.start_date));

    return {
      requests,
      uniquePeople: new Set(requests.map(r => r.user_id)).size,
      totalDays: requests.reduce((sum, req) => sum + (req.calculated_days || 1), 0)
    };
  }, [data?.leave_requests, data?.date_context]);

  // Memoized: Next month's leave
  const nextMonthLeave = useMemo(() => {
    if (!data?.leave_requests || !data?.date_context) return { requests: [], uniquePeople: 0, totalDays: 0 };

    const today = new Date(data.date_context.today);
    const startOfNextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);
    const endOfNextMonth = new Date(today.getFullYear(), today.getMonth() + 2, 0);

    const requests = data.leave_requests
      .filter(req => {
        const start = new Date(req.start_date);
        const end = new Date(req.end_date);
        return start <= endOfNextMonth && end >= startOfNextMonth;
      })
      .sort((a, b) => new Date(a.start_date) - new Date(b.start_date));

    return {
      requests,
      uniquePeople: new Set(requests.map(r => r.user_id)).size,
      totalDays: requests.reduce((sum, req) => sum + (req.calculated_days || 1), 0)
    };
  }, [data?.leave_requests, data?.date_context]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return {
    // Raw data
    data,
    loading,
    error,
    refetch: loadData,

    // Convenience accessors
    teamMembers: data?.team_members || [],
    leaveTypes: data?.leave_types || [],
    leaveRequests: data?.leave_requests || [],
    stats: data?.stats || {
      total_members: 0,
      on_leave_today: 0,
      available: 0,
      with_pending: 0
    },
    dateContext: data?.date_context || null,
    companyWorkingDays: data?.company_working_days || [1, 2, 3, 4, 5],

    // Pre-computed widget data
    offThisWeek,
    upcomingLeave,
    thisMonthLeave,
    nextMonthLeave,

    // Helper functions
    findLeaveTypeConfig,
    shouldHideLeaveType,
    getLeaveTypeColor,
    getLeaveTypeHexColor,
    getLeaveTypeAbbr,
    getUsagePercentage,
    getUsageColor,

    // Static data (module-level constants)
    years: YEARS
  };
}
